import { terminalStringWidth } from "@/lib/terminal-wcwidth";

export interface LineBufferState {
	text: string;
	/** Intl.Segmenterの書記素境界に正規化したUTF-16インデックス。 */
	cursor: number;
}

export interface LineEditorPrompt {
	/** 幅計算に使うANSIエスケープを含まない文字列。 */
	text: string;
	/** xtermへ書き込むプロンプト。ANSIエスケープを含められる。 */
	renderedText: string;
}

export interface LineLayout {
	cursorRow: number;
	cursorCol: number;
	endRow: number;
	endCol: number;
	/** バッファ末尾がxtermのpending wrap状態か。 */
	endPendingWrap: boolean;
	/** 論理行末へカーソルを置くためにpending wrapの実体化が必要か。 */
	needsForcedWrap: boolean;
}

export interface LineFrameInput {
	prompt: LineEditorPrompt;
	/** タブを含む論理入力文字列。描画時だけタブを空白へ展開する。 */
	displayText: string;
	cursor: number;
	cols: number;
	/** 入力行の下に描画する行 (補完候補一覧など)。 */
	menuLines?: string[];
}

export interface LineFrameState extends LineFrameInput {
	layout: LineLayout;
}

const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

function graphemeBoundaries(text: string): number[] {
	const boundaries = Array.from(graphemeSegmenter.segment(text), ({ index }) => index);
	boundaries.push(text.length);
	return boundaries;
}

function boundaryAtOrBefore(text: string, offset: number): number {
	const boundedOffset = Math.max(0, Math.min(offset, text.length));
	let result = 0;
	for (const boundary of graphemeBoundaries(text)) {
		if (boundary > boundedOffset) break;
		result = boundary;
	}
	return result;
}

function boundaryAtOrAfter(text: string, offset: number): number {
	const boundedOffset = Math.max(0, Math.min(offset, text.length));
	return graphemeBoundaries(text).find((boundary) => boundary >= boundedOffset) ?? text.length;
}

function normalizeState(state: LineBufferState): LineBufferState {
	return { text: state.text, cursor: boundaryAtOrBefore(state.text, state.cursor) };
}

export function insertText(state: LineBufferState, insertedText: string): LineBufferState {
	const normalized = normalizeState(state);
	if (insertedText.length === 0) return normalized;
	const text =
		normalized.text.slice(0, normalized.cursor) +
		insertedText +
		normalized.text.slice(normalized.cursor);
	return {
		text,
		cursor: boundaryAtOrAfter(text, normalized.cursor + insertedText.length),
	};
}

export function deleteBackward(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	if (normalized.cursor === 0) return normalized;
	const previous = boundaryAtOrBefore(normalized.text, normalized.cursor - 1);
	return {
		text: normalized.text.slice(0, previous) + normalized.text.slice(normalized.cursor),
		cursor: previous,
	};
}

export function deleteForward(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	if (normalized.cursor === normalized.text.length) return normalized;
	const next = boundaryAtOrAfter(normalized.text, normalized.cursor + 1);
	return {
		text: normalized.text.slice(0, normalized.cursor) + normalized.text.slice(next),
		cursor: normalized.cursor,
	};
}

export function moveLeft(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	return {
		...normalized,
		cursor:
			normalized.cursor === 0 ? 0 : boundaryAtOrBefore(normalized.text, normalized.cursor - 1),
	};
}

export function moveRight(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	return {
		...normalized,
		cursor:
			normalized.cursor === normalized.text.length
				? normalized.cursor
				: boundaryAtOrAfter(normalized.text, normalized.cursor + 1),
	};
}

function currentLineStart(text: string, cursor: number): number {
	return cursor === 0 ? 0 : text.lastIndexOf("\n", cursor - 1) + 1;
}

function currentLineEnd(text: string, cursor: number): number {
	const newline = text.indexOf("\n", cursor);
	return newline === -1 ? text.length : newline;
}

export function moveLineStart(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	return { ...normalized, cursor: currentLineStart(normalized.text, normalized.cursor) };
}

export function moveLineEnd(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	return { ...normalized, cursor: currentLineEnd(normalized.text, normalized.cursor) };
}

export function killToLineStart(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	const start = currentLineStart(normalized.text, normalized.cursor);
	return {
		text: normalized.text.slice(0, start) + normalized.text.slice(normalized.cursor),
		cursor: start,
	};
}

export function killToLineEnd(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	const end = currentLineEnd(normalized.text, normalized.cursor);
	return {
		text: normalized.text.slice(0, normalized.cursor) + normalized.text.slice(end),
		cursor: normalized.cursor,
	};
}

/** カーソル直前の空白列とその前の単語を飛ばした位置 (空白区切りの単語先頭)。 */
function wordStartBefore(text: string, cursor: number): number {
	const segments = Array.from(graphemeSegmenter.segment(text.slice(0, cursor)));
	let start = cursor;
	while (segments.length > 0 && /^\s+$/u.test(segments.at(-1)?.segment ?? "")) {
		start = segments.pop()?.index ?? start;
	}
	while (segments.length > 0 && !/^\s+$/u.test(segments.at(-1)?.segment ?? "")) {
		start = segments.pop()?.index ?? start;
	}
	return start;
}

/** カーソル以降の空白列とその後の単語を飛ばした位置 (空白区切りの単語末尾)。 */
function wordEndAfter(text: string, cursor: number): number {
	const segments = Array.from(graphemeSegmenter.segment(text.slice(cursor)));
	let index = 0;
	while (index < segments.length && /^\s+$/u.test(segments[index].segment)) index++;
	while (index < segments.length && !/^\s+$/u.test(segments[index].segment)) index++;
	return index < segments.length ? cursor + segments[index].index : text.length;
}

export function deleteWordBefore(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	if (normalized.cursor === 0) return normalized;
	const start = wordStartBefore(normalized.text, normalized.cursor);
	return {
		text: normalized.text.slice(0, start) + normalized.text.slice(normalized.cursor),
		cursor: start,
	};
}

export function moveWordLeft(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	return { ...normalized, cursor: wordStartBefore(normalized.text, normalized.cursor) };
}

export function moveWordRight(state: LineBufferState): LineBufferState {
	const normalized = normalizeState(state);
	return { ...normalized, cursor: wordEndAfter(normalized.text, normalized.cursor) };
}

interface LayoutPosition {
	row: number;
	col: number;
	pendingWrap: boolean;
}

const TAB_WIDTH = 4;

function advancePosition(position: LayoutPosition, text: string, cols: number): void {
	for (const { segment } of graphemeSegmenter.segment(text)) {
		if (segment === "\n") {
			// pending wrap中の改行はwrapを実体化し、追加の論理行は作らない。
			if (!position.pendingWrap) position.row++;
			position.col = 0;
			position.pendingWrap = false;
			continue;
		}

		if (segment === "\t") {
			advancePosition(position, " ".repeat(TAB_WIDTH), cols);
			continue;
		}

		const width = terminalStringWidth(segment);
		if (width === 0) continue;
		position.pendingWrap = false;
		const effectiveWidth = Math.min(width, cols);
		if (effectiveWidth > cols - position.col && position.col > 0) {
			position.row++;
			position.col = 0;
		}
		position.col += effectiveWidth;
		if (position.col === cols) {
			position.row++;
			position.col = 0;
			position.pendingWrap = true;
		}
	}
}

export function computeLineLayout(
	prompt: string,
	displayText: string,
	cursor: number,
	cols: number,
): LineLayout {
	const terminalCols = Math.max(1, Math.floor(cols));
	const cursorOffset = boundaryAtOrBefore(displayText, cursor);
	const position: LayoutPosition = { row: 0, col: 0, pendingWrap: false };
	advancePosition(position, prompt, terminalCols);

	let cursorPosition: LayoutPosition | null = cursorOffset === 0 ? { ...position } : null;
	for (const { segment, index } of graphemeSegmenter.segment(displayText)) {
		advancePosition(position, segment, terminalCols);
		if (index + segment.length === cursorOffset) cursorPosition = { ...position };
	}
	cursorPosition ??= { ...position };

	return {
		cursorRow: cursorPosition.row,
		cursorCol: cursorPosition.col,
		endRow: position.row,
		endCol: position.col,
		endPendingWrap: position.pendingWrap,
		needsForcedWrap: cursorOffset === displayText.length && position.pendingWrap,
	};
}

/** プロンプト直後から描画する文字列のタブを、常に4空白へ展開する。 */
export function renderTerminalLineText(prompt: string, text: string, cols: number): string {
	const terminalCols = Math.max(1, Math.floor(cols));
	const position: LayoutPosition = { row: 0, col: 0, pendingWrap: false };
	advancePosition(position, prompt, terminalCols);

	let rendered = "";
	for (const { segment } of graphemeSegmenter.segment(text)) {
		if (segment === "\t") {
			const spaces = " ".repeat(TAB_WIDTH);
			rendered += spaces;
			advancePosition(position, spaces, terminalCols);
			continue;
		}
		rendered += segment;
		advancePosition(position, segment, terminalCols);
	}
	return rendered;
}

function cursorUp(rows: number): string {
	return rows > 0 ? `\u001b[${rows}A` : "";
}

function cursorRight(cols: number): string {
	return cols > 0 ? `\u001b[${cols}C` : "";
}

export function renderLineFrame(
	previous: LineFrameState | null,
	next: LineFrameInput,
): { data: string; frame: LineFrameState } {
	const layout = computeLineLayout(next.prompt.text, next.displayText, next.cursor, next.cols);
	const frame: LineFrameState = { ...next, layout };

	let data = `\r${cursorUp(previous?.layout.cursorRow ?? 0)}\u001b[J`;
	data +=
		next.prompt.renderedText +
		renderTerminalLineText(next.prompt.text, next.displayText, next.cols);
	if (layout.needsForcedWrap) data += "\n";

	// footer はカーソルより下に置くため、次フレーム冒頭の \x1b[J が古い footer を消す。
	const menuLines = next.menuLines ?? [];
	for (const line of menuLines) data += `\r\n${line}`;

	const renderedEndRow = layout.endRow - (layout.endPendingWrap && !layout.needsForcedWrap ? 1 : 0);
	const rowsUp = Math.max(0, renderedEndRow - layout.cursorRow);
	data += `\r${cursorUp(rowsUp + menuLines.length)}${cursorRight(layout.cursorCol)}`;
	return { data, frame };
}
